// services/api.ts
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import {
  type ApiErr,
  // type LoginResponse,
  type RegistrarResponse,
  type Caixa,
  type CaixaCreate,
  type ClienteApi,
  type Produto,
  type ProdutoCreate,
  type ShopifyProduto,
  type LojaCreate,
  CotacaoListResponse,
  PedidosShopifyResp,
  LoginResp,
} from "../types/types";
import { Paginated, Shipment } from "types/shipmentTypes";
import { ehRotaPublica } from "app/rotasPublicas";
import { ACCESS_TOKEN, TOKEN_CSRF } from "./authTokens";
import { BoletoResponse } from "types/Asaas";
import { getSessionToken } from "@shopify/app-bridge-utils";
import { getShopifyAppBridge, isEmbeddedShopifyApp } from "../utils/shopifyAppBridge";

let runtimeCsrfToken: string | null = null;

async function getShopifySessionBearer(): Promise<string | null> {
  if (!isEmbeddedShopifyApp()) return null;

  const app = getShopifyAppBridge();
  if (!app) return null;

  try {
    const token = await getSessionToken(app);
    return token || null;
  } catch (e) {
    console.error("[Shopify Session Token] erro ao obter token:", e);
    return null;
  }
}

function setRuntimeCsrf(token: string | null) {
  runtimeCsrfToken = token;
}

function getRuntimeCsrf(): string | null {
  return runtimeCsrfToken;
}

export type CotacaoCaixaEscolhida = { entryId: string; caixa_id: number };

export type CotacaoCreateRealPayload = {
  pedido_ref: string;
  caixas_escolhidas: CotacaoCaixaEscolhida[];
  moeda_emissao: string;
  moeda_pagamento: string;
  pais_remetente: string;
  pais_dest: string;
};

export type CotacaoCreateRealResponse = {
  ok: boolean;
  cotacao_id: number;
  pedido_ref: string;
  caixas_count: number;
};

export type ItemizedFee = { code?: string; label: string; value: number };

export type CotacaoDetails = {
  id: number;
  pedido_ref: string;
  currency: string;
  base: number;
  total: number;
  serviceOptions?: number;
  itemized: ItemizedFee[];
}

export interface ApiResp<T> {
  ok?: boolean;
  data?: T;
  error?: string;
}

export type MultiQuote = {
  carrier: "UPS" | "FEDEX";
  serviceCode: string;
  serviceLabel: string;
  currency: string;
  total: number;
  base?: number;
  itemized: { code?: string; label: string; value: number }[]
  eta?: { minDays?: number; maxDays?: number; dateStr?: string } | null;
  raw?: any;
}

const API_BASE =
  process.env?.REACT_APP_API_URL || undefined;

console.log("API_BASE >>>", API_BASE);

export const PUBLIC_BASE = String(
  process.env?.VITE_API_BASE_URL || ""
).replace(/\/$/, "");

function getCookie(name: string): string | null {
  const safeName = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const m = document.cookie.match(new RegExp(`(?:^|; )${safeName}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

// Extrai o pathname real mesmo quando config.url é relativo
function getPathname(url?: string): string {
  try {
    if (!url) return "";
    const u = new URL(url, API_BASE);
    return u.pathname;
  } catch {
    // fallback para caminhos relativos simples
    return url || "";
  }
}

function getCsrfToken() {

  const fromCookie = getCookie("csrf_token")
  if (fromCookie) return fromCookie;
  // primeiro tenta pegar do localStorage (funciona em prod)
  const fromStorage = localStorage.getItem(TOKEN_CSRF);
  if (fromStorage) return fromStorage;

  // fallback: em dev (localhost) ainda funciona via cookie
  return null;
}

function getCsrfTokenFromCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export const API = axios.create({
  baseURL: API_BASE ? API_BASE.replace(/\/$/, "") : undefined,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

const NO_CSRF_URLS = ["/login", "/auth/login", "/auth/refresh"];

// Endpoints de auth (para não tentar refresh em cima deles)
const AUTH_URLS = ["/auth/refresh", "/auth/login", "/login"];

API.interceptors.request.use(async (config) => {
  config.headers = config.headers ?? {};

  const shopifySessionToken = await getShopifySessionBearer();

  if (shopifySessionToken) {
    config.headers.Authorization = `Bearer ${shopifySessionToken}`;
  } else {
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  const method = (config.method || "get").toUpperCase();
  const path = getPathname(config.url);

  const needsCsrf =
    ["POST", "PUT", "PATCH", "DELETE"].includes(method) &&
    !NO_CSRF_URLS.some((p) => path.startsWith(p));

  if (needsCsrf) {
    const csrf = getCsrfToken() || getRuntimeCsrf();

    if (csrf) {
      (config.headers as any)["x-csrf-token"] = csrf;
    }
  }

  if (config.data instanceof FormData) {
    delete (config.headers as any)["Content-Type"];
  }

  return config;
});

let isRefreshing = false;
let waiters: Array<(ok: boolean) => void> = [];

/*
 * Sessão caída leva para o login — menos quando a rota é pública.
 *
 * Quem abre intrex.com.br cai em /sobre sem sessão nenhuma, e o `/me` que o
 * AuthProvider dispara ao montar responde 401 justamente por isso. Esse 401
 * reescrevia a barra de endereço para /login dois segundos depois de abrir o
 * site, enquanto a página continuava sendo a Sobre. Visitante deslogado numa
 * página aberta não é sessão expirada.
 *
 * Nas rotas privadas segue com replaceState, que é o comportamento atual. Vale
 * registrar que ele não navega: troca a URL sem o React Router saber, então a
 * tela privada continua montada sob um endereço que diz /login. Consertar isso
 * é uma correção à parte, com risco próprio nos fluxos de sessão expirada.
 */
function levarParaLogin() {
  const atual = window.location.pathname;
  if (ehRotaPublica(atual)) return;
  if (atual !== "/login") {
    window.history.replaceState(null, "", "/login");
  }
}

async function refreshAccess(): Promise<boolean> {
  try {
    await API.post("/auth/refresh"); // cookies HttpOnly enviados com withCredentials
    return true;
  } catch {
    return false;
  }
}
// const AUTH_URLS = ['/auth/refresh', '/auth/login', '/login'];
API.interceptors.response.use(
  (response) => {
    const path = getPathname(response.config?.url);

    // se a resposta for do /login ou /auth/login, captura o csrfToken
    if (path === "/login" || path?.startsWith("/auth/login")) {
      const data: any = response.data;
      if (data && data.csrfToken) {
        setRuntimeCsrf(data.csrfToken || null);
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    const original = (error.config ?? {}) as AxiosRequestConfig & { _retry?: boolean };
    const status = error?.response?.status ?? 0;
    const path = getPathname(original.url);

    // Se não for 401, só propaga o erro
    if (status !== 401) return Promise.reject(error);

    // Se o endpoint é de auth, não tentar refresh — leve o usuário para /login (sem reload)
    if (AUTH_URLS.some((p) => path.startsWith(p))) {
      levarParaLogin();
      return Promise.reject(error);
    }

    // Evitar múltiplas tentativas no mesmo request
    if (original._retry) return Promise.reject(error);
    original._retry = true;

    // Se já há um refresh em progresso, aguarde
    if (isRefreshing) {
      const ok = await new Promise<boolean>((resolve) => waiters.push(resolve));
      if (!ok) {
        levarParaLogin();
        return Promise.reject(error);
      }
      return API(original);
    }

    // Inicia refresh
    isRefreshing = true;
    const ok = await refreshAccess();
    isRefreshing = false;

    // Notifica todos os aguardando
    waiters.forEach((fn) => fn(ok));
    waiters = [];

    if (!ok) {
      levarParaLogin();
      return Promise.reject(error);
    }

    // Reexecuta a chamada original com o novo access token
    return API(original);
  }
);

/** Tipos utilitários */
type ValidaCNPJResp = { valid: boolean; exists?: boolean; reason?: string };

/** VALIDADORES */
export const validarCNPJ = (cnpj: string) => {
  const clean = String(cnpj || "").replace(/\D/g, "");
  return API.get<ValidaCNPJResp>("/validate/cnpj", { params: { cnpj: clean } });
};

export const validarCNPJonline = (cnpj: string, online?: "receitaws") => {
  const clean = String(cnpj || "").replace(/\D/g, "");
  return API.get<ValidaCNPJResp>("/validate/cnpj", {
    params: { cnpj: clean, online },
  });
};

/** LOGIN / REGISTRAR */
export type RegistrarPayload = {
  emailPrincipal: string;
  senha: string;
  tipoConta: "parceiro" | "empresa";
  emailAssociado: string;
  codigo: string;
  razaoSocial: string;
  enderecoPais: string;
  enderecoCEP: string;
  enderecoRua: string;
  enderecoNumero: string;
  enderecoCidade: string;
  enderecoEstado: string;
  tipoDocumento?: "cnpj" | "cpf" | "estrangeiro";
  cnpj: string;
  cnaePrincipal: string;
  telefoneCelular: string;
  enderecoComplemento?: string;
  plano?: string;
  descIOR?: string;
  nomeIOR?: string;
  emailIOR?: string;
  tipoIOR?: string;
  paisIOR?: string;
  estadoIOR?: string;
  cidadeIOR?: string;
  enderecoIOR?: string;
  numeroIOR?: string;
  cod_postalIOR?: string;
  telefoneIOR?: string;
  state_tax_idIOR?: string;
  destinoFixoNome?: string;
  destinoFixoPais?: string;
  destinoFixoEstado?: string;
  destinoFixoCidade?: string;
  destinoFixoRua?: string;
  destinoFixoCEP?: string;
  destinoFixoTelefone?: string;
};

// const TOKEN_CSRF = "INTREX_CSRF_TOKEN";
// const ACCESS_TOKEN_CSRF = "INTREX_ACCESS_TOKEN"

export const registrarCliente = (dados: RegistrarPayload) =>
  API.post<RegistrarResponse>("/registrarClientes", dados);

// Importante: /login NÃO recebe x-csrf-token (ver interceptor)
export const loginCliente = async (dados: { emailPrincipal: string; senha: string }) => {
  const res = await API.post<LoginResp>("/login", dados);
  const { csrfToken, accessToken } = res.data;

  console.log("[loginCliente] res.data =", res.data); // DEBUG


  setRuntimeCsrf(csrfToken || null);

  if (csrfToken) {
    localStorage.setItem(TOKEN_CSRF, csrfToken)
  }

  if (accessToken) {
    API.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    localStorage.setItem(ACCESS_TOKEN, accessToken);
  }

  // se quiser já devolver só o cliente, pode devolver res.data.cliente
  return res.data;
};
/** CLIENTES */
export const verClienteAtual = () =>
  API.get<ClienteApi>("/verClienteAtual");

export const me = () => API.get<ClienteApi>("/me");

/** CAIXAS */
export const verCaixas = () => API.get<Caixa[] | ApiErr>("/verCaixas");
export const excluirCaixa = (id: number) => API.delete(`/excluirCaixa/${id}`);
export const cadastrarCaixa = (dados: CaixaCreate) =>
  API.post("/registrarCaixa", dados);
export const editarCaixa = (id: number, dados: any) =>
  API.put(`/editarCaixa/${id}`, dados);

/** PRODUTOS */
export const verProdutos = () =>
  API.get<Produto[] | ApiErr>("/verProdutos");
export const cadastrarProduto = (dados: ProdutoCreate) =>
  API.post("/registrarProduto", dados);
export const excluirProduto = (id: number) =>
  API.delete(`/excluirProduto/${id}`);
export const editarProduto = (id: number, dados: any) =>
  API.put(`/editarProduto/${id}`, dados);

// COTACOES

/**
 * Cancela a cotação. Não apaga mais a linha: ela passa a constar como
 * CANCELADA, porque o estorno precisa de uma referência que continue existindo.
 * `estorno_centavos` vem > 0 quando o valor pago voltou para a carteira.
 */
export const excluirCotacao = (id: number) =>
  API.delete<{
    ok: boolean;
    deleted: boolean;
    cancelada?: boolean;
    estorno_centavos?: number;
    forma_pagamento?: "carteira" | "boleto" | null;
  }>(`/api/cotacoes/${id}`);

/** Cria uma cotação (mock da transportadora).
 *  O cliente é obtido do token (Authorization Bearer).
 */
// Cria a cotação e salva tudo no BD
export const criarCotacao = (payload: CotacaoCreateRealPayload) =>
  API.post<CotacaoCreateRealResponse>("/api/cotacoes", payload);

// Lista cotações do cliente atual
export const listarCotacoes = (params?: {
  limit?: number;
  offset?: number;
  pais_dest?: string;
  moeda?: string;
  pedido_ref?: string;
  refresh?: '1' | '0';
}) =>
  API.get<CotacaoListResponse>("/api/cotacoes", {
    params: {
      limit: params?.limit ?? 20,
      offset: params?.offset ?? 0,
      pais_dest: params?.pais_dest,
      moeda: params?.moeda,
      pedido_ref: params?.pedido_ref,
      refresh: params?.refresh ?? '1',
    },
  });

export function verCotacaoPorId(id: number | string) {
  return API.get(`/api/cotacoes/${id}`);
}


/** Helper opcional para montar payload a partir de seleção do UI */
// Monta payload real a partir do estado do UI
export function montarPayloadCotacaoReal(opts: {
  pedido_ref: string;
  cotacaoCaixas: { entryId: string; data: { id: number | string } }[];
  moeda_emissao?: string;
  moeda_pagamento?: string;
  pais_remetente?: string;
  pais_dest?: string;
}): CotacaoCreateRealPayload {
  if (!opts.pedido_ref) throw new Error("pedido_ref é obrigatório");
  if (!opts.cotacaoCaixas?.length) throw new Error("Adicione ao menos uma caixa");

  const caixas_escolhidas = opts.cotacaoCaixas.map((c) => ({
    entryId: c.entryId,
    caixa_id: Number(c.data.id),
  }));

  return {
    pedido_ref: opts.pedido_ref,
    caixas_escolhidas,
    moeda_emissao: opts.moeda_emissao ?? "USD",
    moeda_pagamento: opts.moeda_pagamento ?? "BRL",
    pais_remetente: opts.pais_remetente ?? "US",
    pais_dest: opts.pais_dest ?? "BR",
  };
}

/** SHOPIFY */

// salva/associa a loja ao cliente do seu sistema
export const conectarLoja = (dados: LojaCreate) =>
  API.post(`/conectarLoja`, dados);

// seta o cookie bind_cliente_id no backend e retorna a URL de OAuth
export const prepareShopifyInstall = (shop: string) =>
  API.post<{ authUrl: string }>("/shopify/prepare-install", { shop });

// checa se o cliente atual tem loja conectada
export const verConexaoShopify = () =>
  API.get<{ connected: boolean; loja?: any }>("/shopify/conexao");

/** NUVEMSHOP */
export const prepareNuvemshopInstall = () =>
  API.post<{ authUrl: string }>("/nuvemshop/prepare-install");

export const verConexaoNuvemshop = () =>
  API.get<{ connected: boolean; storeId?: string }>("/nuvemshop/conexao");

export type NuvemshopResumo = {
  connected: boolean;
  storeId?: string | null;
  store?: { name: string | null; email: string | null; url: string | null } | null;
  carrier?: {
    registered: boolean;
    active: boolean | null;
    upsOptions: number | null;
    fedexOptions: number | null;
  } | null;
  warnings?: string[];
};

export const verResumoNuvemshop = () =>
  API.get<NuvemshopResumo>("/nuvemshop/resumo");

export type NuvemshopResumoEmbed = NuvemshopResumo & {
  stats?: { totalCotacoes: number; entregues: number; emAndamento: number } | null;
};

export const verResumoEmbedNuvemshop = (storeId: string) =>
  API.get<NuvemshopResumoEmbed>("/nuvemshop/resumo-embed", { params: { store_id: storeId } });

export type TipoDescontoFrete = "gratis" | "percentual" | "fixo";

export type RegraFrete = {
  id: number;
  nome: string;
  ativa: boolean;
  valorMinimo: number;
  transportadoras: string[];
  tipoDesconto: TipoDescontoFrete;
  valorDesconto: number | null;
  prioridade: number;
};

export type RegraFretePayload = Omit<RegraFrete, "id">;

export const listarRegrasFrete = (storeId: string) =>
  API.get<RegraFrete[]>("/nuvemshop/regras-frete", { params: { store_id: storeId } });

export const criarRegraFrete = (storeId: string, payload: RegraFretePayload) =>
  API.post<RegraFrete>("/nuvemshop/regras-frete", { ...payload, store_id: storeId });

export const atualizarRegraFrete = (storeId: string, id: number, payload: RegraFretePayload) =>
  API.put<RegraFrete>(`/nuvemshop/regras-frete/${id}`, { ...payload, store_id: storeId });

export const excluirRegraFrete = (storeId: string, id: number) =>
  API.delete<{ ok: boolean }>(`/nuvemshop/regras-frete/${id}`, { params: { store_id: storeId } });

export const registrarCarrierNuvemshop = () =>
  API.post<{ status: number; body: any; options: any[] }>("/nuvemshop/registrar-carrier");

// cache simples do domínio da loja do cliente
let cachedShopDomain: string | null = null;

async function ensureShopDomain(): Promise<string> {
  if (cachedShopDomain) return cachedShopDomain;
  const { data } = await verConexaoShopify(); // GET /shopify/conexao
  if (!data.connected || !data.loja?.shopDomain) {
    throw new Error("Nenhuma loja conectada para este usuário.");
  }
  cachedShopDomain = String(data.loja.shopDomain).toLowerCase();
  return cachedShopDomain;
}

/**
 * Consulta produtos na Shopify (usa sua API /shopify/produtos).
 * Obs: sua rota /shopify/produtos usa autenticarShopify + comLoja + garantirInstalada;
 *      se rodar fora do Admin, pode dar 401 (porque exige session token).
 */
export const produtosShopify = async (
  limite = 250,
  fields = "id,title,handle,product_type,category,status,updated_at,variants"
): Promise<ShopifyProduto[] | ApiErr> => {
  const shop = await ensureShopDomain();
  const res = await API.get<{ produtos: ShopifyProduto[]; nextPage?: string | null } | ApiErr>(
    "/shopify/produtos",
    { params: { shop, limite: String(limite), fields } }
  );
  const data = res.data as any;
  if (data?.erro) return data as ApiErr;
  return (data?.produtos as ShopifyProduto[]) || [];
};

/** Helper pra montar URL ABSOLUTA de instalação (grant) no front:
 *  `${PUBLIC_BASE}/shopify/auth?shop=${encodeURIComponent(shop)}`
 *
 *  Ex.: window.top!.location.replace(makeInstallUrl('minha-loja.myshopify.com'))
 */
export function makeInstallUrl(shopDomain: string) {
  return `${PUBLIC_BASE}/shopify/auth?shop=${encodeURIComponent(
    shopDomain.toLowerCase()
  )}`;
}

export async function uploadOrdersMinimal(file: File) {
  const form = new FormData();
  form.append("file", file);

  const csrfToken = getCsrfTokenFromCookie();
  console.log("#########chegou aqui uploadOrdersMinimal1#########");


  const { data } = await API.post("/shopify/import-pedidos", form, {
    headers: csrfToken ? { "x-csrf-token": csrfToken } : undefined,
  });
  console.log("#########chegou aqui uploadOrdersMinimal2#########");

  return data;
}

export const pedidosShopify = async (opts?: {
  limit?: number;
  page_info?: string | null;
}): Promise<PedidosShopifyResp> => {
  const shop = await ensureShopDomain();
  const { data } = await API.get<PedidosShopifyResp>("/shopify/pedidos", {
    params: {
      shop,
      limit: opts?.limit ?? 20,
      page_info: opts?.page_info ?? undefined,
    },
  });
  return data;
};

export const criarRemessa = (payload: {
  carrier: string;
  status: string;
  rate_result: any;
  ship_result: any;
  track_result?: any;
  shop_id?: number | null;
}) => API.post<Shipment>("/api/ups/shipments", payload);

/** Lista remessas do cliente atual */
export const listarRemessas = (opts?: { page?: number; pageSize?: number }) =>
  API.get<Paginated<Shipment>>("/api/ups/shipments", {
    params: { page: opts?.page ?? 1, pageSize: opts?.pageSize ?? 20 },
  });

/** Detalhe da remessa */
export const verRemessa = (id: number | string) =>
  API.get<Shipment>(`/api/shipments/${id}`);

/** Atualiza somente o track_result (quando o usuário clicar “Rastrear”) */
export const atualizarTrackRemessa = (
  id: number | string,
  track_result: any
) => API.patch<Shipment>(`/api/shipments/${id}`, { track_result });

export const checarCotacaoPorPedido = (pedido_ref: string) =>
  API.get<{ ok: true; hasActive: boolean; cotacaoId: number | null }>(
    `/api/cotacoes/status-por-pedido/${encodeURIComponent(pedido_ref)}`
  );

export const DEFAULT_TRACK_TEXT =
  "The package is at the clearing agency awaiting final release.";

export function deriveShipmentLastText(s?: Shipment | null): string {
  const acts =
    s?.track_result?.trackResponse?.shipment?.[0]?.package?.[0]?.activity || [];
  const last = acts[0];
  return last?.status?.description || DEFAULT_TRACK_TEXT;
}

export function deriveShipmentStatus(
  s?: Shipment | null
): "created" | "label_ready" | "in_transit" | "delivered" | "exception" | "hold" {
  if (!s) return "created";
  if (!s.track_result) return s.ship_result ? "label_ready" : "created";
  const acts =
    s.track_result?.trackResponse?.shipment?.[0]?.package?.[0]?.activity || [];
  const last = acts[0];
  const type = last?.status?.type || "";
  const desc = String(last?.status?.description || "").toLowerCase();
  if (type === "D" || desc.includes("delivered")) return "delivered";
  if (type === "X" || desc.includes("exception") || desc.includes("unable") || desc.includes("return")) return "exception";
  if (desc.includes("clearing agency") || desc.includes("pending release") || desc.includes("government agency") || desc.includes("customs")) return "hold";
  return "in_transit";
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch("/api" + path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init
  });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(text || `HTTP ${r.status}`);
  }
  return r.json();
}

export const api = { req };

export type CotacaoDocsPayload = {
  etiqueta_base64?: string;
  etiqueta_mime?: string;     // 'image/png' | 'image/gif' | 'application/pdf' | 'text/plain' (ZPL se quiser guardar)
  invoice_base64?: string;
  invoice_mime?: string;      // 'application/pdf'
  etiqueta_url?: string;
  invoice_url?: string;
  tracking_number?: string;
  // Envio multi-volume: uma entrada por caixa. O backend junta tudo num PDF
  // com uma página por volume e guarda como a etiqueta da cotação.
  etiquetas?: Array<{ base64?: string; url?: string; mime?: string }>;
};

// --- acrescente esta função junto dos exports de cotação ---
// OBS: backend expõe POST /cotacoes/:id/docs (como combinamos no controller)
export const anexarDocsCotacao = (cotacaoId: number, body: CotacaoDocsPayload) =>
  API.post<{ ok: boolean; cotacao_id: number }>(`/api/cotacoes/${cotacaoId}/docs`, body);


// Invoice emitida pela Intrex, para envio que a transportadora nao documentou.
export const gerarInvoiceCotacao = (cotacaoId: number) =>
  API.post<{ ok: boolean; cotacao_id: number; has_invoice: boolean }>(
    `/api/cotacoes/${cotacaoId}/invoice/gerar`,
  );

/*
 * Formulário CPSC "Goods Not Registered" da FedEx, pedido pelo despachante
 * no desembaraço.
 *
 * O que a cotação não sabe viaja aqui: código de disclaim, intended use code
 * e dados de laboratório são declarações de quem assina, não dados do
 * pedido. Por isso vão na requisição e o formulário é regerável — corrigir
 * uma resposta não exige cotação nova.
 *
 * `disclaimer: null` significa NÃO disclamar, e é o que abre as Partes II a
 * IV do formulário. Omitir o campo cai no padrão, que é a opção B.
 */
export type CpscOpcoes = {
  disclaimer?: "A" | "B" | "NAO_COMERCIAL" | null;
  intendedUseCode?: string;
  /** Campo "Description" da Parte I. Vazio, sai a descrição do item. */
  intendedUseDescricao?: string;
  certificacaoExiste?: boolean;
  tipoCertificado?: "CPC" | "GCC" | null;
  regraOuExclusao?: string;
  submitterNome?: string;
  submitterTitulo?: string;
  /** Campo 41. Vazio por padrão: assinatura é ato de quem assina. */
  submitterAssinatura?: string;
};

export const gerarCpscCotacao = (cotacaoId: number, opcoes: CpscOpcoes = {}) =>
  API.post<{ ok: boolean; cotacao_id: number; has_cpsc: boolean; formularios: number }>(
    `/api/cotacoes/${cotacaoId}/cpsc/gerar`,
    opcoes,
  );

export const baixarCpscCotacao = async (id: number | string) => {
  const resp = await API.get(`/api/cotacoes/${id}/cpsc`, { responseType: 'blob' });
  return resp.data as Blob;
};

export const baixarEtiquetaCotacao = async (id: number | string) => {
  const resp = await API.get(`/api/cotacoes/${id}/etiqueta`, { responseType: 'blob' });
  return resp.data as Blob;
};
export const baixarInvoiceCotacao = async (id: number | string) => {
  const resp = await API.get(`/api/cotacoes/${id}/invoice`, { responseType: 'blob' });
  return resp.data as Blob;
};

export const verCotacaoDetalhes = (id: number | string): Promise<AxiosResponse<ApiResp<CotacaoDetails>>> =>
  API.get(`/api/cotacoes/${id}/details`);

export async function baixarRelatorioPagamentos(
  { from, to, tipo = "csv" }: { from?: string; to?: string; tipo?: string } = {}
) {
  const url = `/api/relatorio/pagamentos.${tipo}`;

  const resp = await API.post(url, { from, to }, { responseType: "blob" });
  const contentType = resp.headers["content-type"] || (tipo === "xlsx"
    ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    : "text/csv; charset=utf-8");

  // tenta pegar filename do header
  const cd = resp.headers["content-disposition"] || "";
  const m = cd.match(/filename="?([^"]+)"?/i);
  const filename = m?.[1] || `relatorio-pagamentos-${from || "ini"}_${to || "fim"}.${tipo}`;

  const blob = new Blob([resp.data], { type: contentType });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

export async function cotarMulti(payload: any): Promise<{ quotes: MultiQuote[]; warnings?: string[] }> {
  const { data } = await API.post<{ quotes: MultiQuote[]; warnings?: string[] }>("/api/rate/multi", payload);
  return data;
}

// Shipments (novo fluxo compare/confirm)
export const compararFretesShipment = (payload: any) =>
  API.post("/api/shipments/compare", payload);

export const confirmarFreteShipment = (shipmentId: number, carrier: "UPS" | "FEDEX") =>
  API.post(`/api/shipments/${shipmentId}/confirm`, { carrier });

export function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00"); //se data for 2025-11-01 vai virar => "2025-11-01T00:00:00"
  d.setDate(d.getDate() + days); //addDays("2025-11-01", 14) → vira 15/11/2025
  const yyyy = d.getFullYear(); //se d = new Date("2025-11-01"), então yyyy = 2025
  const mm = String(d.getMonth() + 1).padStart(2, "0");// Se fosse "2025-03-01", d.getMonth() = 2 → +1 = 3 → String("3").padStart(2, "0") = "03"
  const dd = String(d.getDate()).padStart(2, "0");// Para "2025-11-15", d.getDate() = 15 → vira "15"
  return `${yyyy}-${mm}-${dd}`;
}//add days

export const gerarBoleto15Dias = async (
  input: { dataFinal: string; dueDate?: string; clienteId?: number }
): Promise<BoletoResponse> => {
  const { dataFinal, clienteId } = input;

  const from = addDays(dataFinal, -14); // 15 dias (contando o primeiro)
  const to = dataFinal;

  const dueDate = addDays(dataFinal, 5); //vencimento no último dia do período

  const body: any = {
    from,
    to,
    dueDate: dueDate, // pode vencer no último dia do período, por ex
  };

  if (clienteId) body.clienteId = clienteId;

  const { data } = await API.post<BoletoResponse>("/boletos", body);
  return data;
};

export function verDolar() {
  return API.get("/dolar");      // bate no backend, não na AwesomeAPI
}

export type CotacaoRemetenteResp = {
  ok: boolean;
  remetente: {
    nome: string;
    telefone: string;
    email: string;
    rua: string;
    numero: string;
    complemento?: string | null;
    cidade: string;
    estado: string;
    cep: string;
    pais: string;
    cnpjOuTaxId: string;
  };
};

export const verRemetenteCotacao = (id: number | string) =>
  API.get<CotacaoRemetenteResp>(`/api/cotacoes/${id}/remetente`);
